// tooltip.ts — the console's tooltip seam (design-system-foundation).
//
// A RE-EXPORT of the shared pi-stack implementation, not a copy: one floating
// layer per root, delegated off a stable mount root so full-innerHTML
// rebuilds never re-wire anything, driven by data attributes:
//
//   data-tip        — plain-text tooltip (replaces native `title`)
//   data-tip-agents — rich popover; HTML from the caller's provider at hover
//
// Elements carrying these MUST NOT also carry `title` (double tooltip).
//
// The CSS twin lives in styles.css as `.bridge-tip`, authored in the
// console's own --hc-* tokens — the console's :root has NO floor for the
// Bridge's raw --surface/--ink vocabulary, so the borrowed rule would render
// unstyled on a first-ever visit if copied verbatim.
//
// MOUNTED ONCE, above the router, on the persistent #app root (app.ts): that
// one delegated listener covers home, users AND audit, so every downstream
// consumer (active-users-column's R38, framework-column's R19 tooltip,
// grid-toolbar's R33 console half) can rely on the listener existing
// regardless of merge order. installTooltips returns the teardown; the boot
// mount deliberately outlives every view (there is exactly one listener and
// one layer, so route swaps leak nothing).

export { installTooltips } from 'pi-stack/client/tooltip.js';
export type { TipProviders } from 'pi-stack/client/tooltip.js';

// ---------------------------------------------------------------------
// The console's rich-tip registry (bridge-ui-polish follow-up): the html
// provider lane is mounted ONCE with the app-wide listener (app.ts), but
// the DATA lives in whichever view is mounted — so views register a
// prefix at mount and hand back fresh HTML at hover time (the provider
// contract: always current, provider owns escaping). Keys are
// `<prefix>:<rest>`; a prefix nobody holds resolves null, and the layer
// degrades to the element's own data-tip.

type RichTipFn = (rest: string) => string | null;
const richTips = new Map<string, RichTipFn>();

/** Register a prefix; returns the unregister (views call it on teardown). */
export function registerRichTip(prefix: string, fn: RichTipFn): () => void {
  richTips.set(prefix, fn);
  return () => {
    if (richTips.get(prefix) === fn) richTips.delete(prefix);
  };
}

/** The ONE provider app.ts hands to installTooltips. */
export function resolveRichTip(key: string): string | null {
  const i = key.indexOf(':');
  const fn = richTips.get(i === -1 ? key : key.slice(0, i));
  return fn ? fn(i === -1 ? '' : key.slice(i + 1)) : null;
}
